import i18n from 'modules/core/lib/i18n'

import type { BranchOperation } from 'modules/automations/lib/Store/Branch'
import { TriggerAttributes } from 'modules/automations/lib/triggerAttributes'
import { Action } from 'modules/automations/store/models/Action'
import type CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'
import type CustomField from 'modules/custom_resources/store/models/customFields/Field'

// Types of attributes that represent custom data
const CUSTOM_DATA = [TriggerAttributes.custom_fields, TriggerAttributes.custom_resources]

function getTranslationByAttribute(attribute: string): string {
  switch (attribute) {
    case 'author_id':
      return i18n.t('automations.add_block.condition_types.document_author')
    case 'assignee_id':
      return i18n.t('automations.add_block.condition_types.document_assignee')
    case 'send_notifications':
      return i18n.t('automations.add_block.condition_types.send_notification_toggle')
    case 'document_public':
      return i18n.t('automations.add_block.condition_types.document_public')
    case 'folder_id':
      return i18n.t('automations.add_block.condition_types.folder_id')
    case 'leave_type_id':
      return i18n.t('automations.add_block.condition_types.leave_type')
    case 'leave_type':
      return i18n.t('automations.add_block.condition_types.leave_type')
    case 'review_request_state':
      return i18n.t('automations.add_block.condition_types.review_request_state')
    case 'first_name':
      return i18n.t('automations.add_block.condition_types.first_name')
    case 'last_name':
      return i18n.t('automations.add_block.condition_types.last_name')
    case 'employee_id':
      return i18n.t('automations.add_block.condition_types.full_name')
    case 'gender':
      return i18n.t('automations.add_block.condition_types.gender')
    case 'nationality':
      return i18n.t('automations.add_block.condition_types.nationality')
    case 'age':
      return i18n.t('automations.add_block.condition_types.age')
    case 'birthday':
      return i18n.t('automations.add_block.condition_types.date_of_birth')
    case 'city':
      return i18n.t('automations.add_block.condition_types.city')
    case 'state':
      return i18n.t('automations.add_block.condition_types.state')
    case 'country':
      return i18n.t('automations.add_block.condition_types.country')
    case 'identifier':
      return i18n.t('automations.add_block.condition_types.id_number')
    case 'social_security_number':
      return i18n.t('automations.add_block.condition_types.social_security_number')
    case 'bank_number':
      return i18n.t('automations.add_block.condition_types.bank_number')
    case 'location_id':
      return i18n.t('automations.add_block.condition_types.office')
    case 'team_id':
      return i18n.t('automations.add_block.condition_types.team')
    case 'manager_id':
      return i18n.t('automations.add_block.condition_types.manager')
    case 'timeoff_manager_id':
      return i18n.t('automations.add_block.condition_types.timeoff_manager')
    case 'has_payroll':
      return i18n.t('automations.add_block.condition_types.contract.has_payroll')
    case 'starts_on':
      return i18n.t('automations.add_block.condition_types.contract.starts_on')
    case 'ends_on':
      return i18n.t('automations.add_block.condition_types.contract.ends_on')
    case 'active':
      return i18n.t('automations.add_block.condition_types.employee_status')
    case 'address_line_1':
      return i18n.t('automations.add_block.condition_types.address_line_1')
    case 'address_line_2':
      return i18n.t('automations.add_block.condition_types.address_line_2')
    case 'postal_code':
      return i18n.t('automations.add_block.condition_types.postal_code')
    case 'job_title':
      return i18n.t('automations.add_block.condition_types.contract.job_title')
    case 'job_catalog_level_id':
      return i18n.t('contract_versions.navigator.changes.job_catalog_level_id')
    case 'salary_amount':
      return i18n.t('automations.add_block.condition_types.contract.salary_amount')
    case 'working_hours':
      return i18n.t('automations.add_block.condition_types.contract.working_hours')
    case 'working_hours_frequency':
      return i18n.t('automations.add_block.condition_types.contract.working_hours_frequency')
    case 'salary_frequency':
      return i18n.t('contract_versions.navigator.changes.salary_frequency')
    case 'working_week_days':
      return i18n.t('contract_versions.navigator.changes.working_week_days')
    case 'contracts_es_contract_type_id':
      return i18n.t('contract_versions.navigator.changes.es_contract_type_id')
    case 'es_contract_observations':
      return i18n.t('contract_versions.navigator.changes.es_contract_observations')
    case 'es_cotization_group_id':
      return i18n.t('contract_versions.navigator.changes.es_cotization_group_id')
    case 'contracts_es_education_level_id':
      return i18n.t('contract_versions.navigator.changes.es_education_level_id')
    case 'es_has_teleworking_contract':
      return i18n.t('contract_versions.navigator.changes.es_has_teleworking_contract')
    case 'es_job_description':
      return i18n.t('contract_versions.navigator.changes.es_job_description')
    case 'contracts_es_professional_category_id':
      return i18n.t('contract_versions.navigator.changes.es_professional_category_id')
    case 'trial_period':
      return i18n.t('contract_versions.navigator.changes.trial_period')
    case 'trial_period_ends_on':
      return i18n.t('contract_versions.navigator.changes.trial_period_ends_on')
    case 'contracts_es_working_day_type_id':
      return i18n.t('contract_versions.navigator.changes.es_working_day_type_id')
    case 'fr_coefficient':
      return i18n.t('contract_versions.navigator.changes.fr_coefficient')
    case 'contracts_fr_contract_type_id':
      return i18n.t('contract_versions.navigator.changes.fr_contract_type_id')
    case 'fr_employee_type':
      return i18n.t('contract_versions.navigator.changes.fr_employee_type')
    case 'fr_forfait_jours':
      return i18n.t('contract_versions.navigator.changes.fr_forfait_jours')
    case 'contracts_fr_level_id':
      return i18n.t('contract_versions.navigator.changes.fr_level_id')
    case 'contracts_fr_mutual_id':
      return i18n.t('contract_versions.navigator.changes.fr_mutual_id')
    case 'fr_jours_par_an':
      return i18n.t('contract_versions.navigator.changes.fr_jours_par_an')
    case 'contracts_fr_professional_category_id':
      return i18n.t('contract_versions.navigator.changes.fr_professional_category')
    case 'contracts_fr_step_id':
      return i18n.t('contract_versions.navigator.changes.fr_step_id')
    case 'fr_work_type_id':
      return i18n.t('contract_versions.navigator.changes.fr_work_type_id')
    case 'incidence_message_id':
      return i18n.t('automations.add_block.condition_types.incidence_message_id')
    case 'contracts_de_contract_type_id':
      return i18n.t('contract_versions.navigator.changes.de_contract_type_id')
    case 'contracts_pt_contract_type_id':
      return i18n.t('contract_versions.navigator.changes.pt_contract_type_id')
    default:
      return ''
  }
}

export function isCustomField(operation: BranchOperation): boolean {
  const attr = operation.attribute

  return CUSTOM_DATA.includes(attr)
}

export function getCustomField(
  operation: BranchOperation,
  customFieldsCollection: CustomFieldsCollection
): CustomField | undefined {
  if (!isCustomField(operation)) return

  const id = operation.id

  return customFieldsCollection.find({
    id,
  })
}

export function getActionName(
  action: Action | undefined,
  customFieldsCollection?: CustomFieldsCollection,
  forHistory?: boolean
): string {
  switch (action?.attributes.actionable_type) {
    case Action.Types.requestReview:
      return i18n.t('automations.actions.request_review.title')

    case Action.Types.sendNotification:
      return (
        action.notifier?.attributes.title ??
        i18n.t('automations.actions.send_notification.title_default')
      )

    case Action.Types.sendCustomNotification:
      return i18n.t('automations.actions.send_custom_notification.title')

    case Action.Types.branch: {
      const initialText = forHistory ? i18n.t('automations.steps.condition') + ': ' : ''
      const operation: BranchOperation = action.attributes.metadata.operations[0]
      const attr = operation.attribute

      if (isCustomField(operation)) {
        const customField =
          customFieldsCollection && getCustomField(operation, customFieldsCollection)

        return (
          initialText +
          (customField ? customField.get('label') : i18n.t('automations.add_block.missing_field'))
        )
      }

      return initialText + getTranslationByAttribute(attr)
    }

    case Action.Types.time:
      return i18n.t('automations.triggers.time.title')

    default:
      return i18n.t('automations.actions.unknown_action')
  }
}
